import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, User, Calendar, CheckCircle2, FileText, MapPin, Image as ImageIcon, ShieldCheck } from 'lucide-react';
import { fetchSurveyResponseById } from '../services/surveyService';
import { fetchDisclosureForResponse, AttributeDisclosure } from '../services/disclosureService';
import { fetchAttributeDefinitions } from '../services/attributesService';
import { AttributeDefinition } from '../services/zkVault/types';
import { SurveyResponse, Survey, LocalizedText } from '../types';

const SurveyResponseResultView: React.FC = () => {
    const { responseId } = useParams<{ responseId: string }>();
    const navigate = useNavigate();

    const [response, setResponse] = useState<(SurveyResponse & { survey: Survey }) | null>(null);
    const [disclosure, setDisclosure] = useState<AttributeDisclosure | null>(null);
    const [defs, setDefs] = useState<AttributeDefinition[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (responseId) {
            loadData();
        }
    }, [responseId]);

    const loadData = async () => {
        setLoading(true);
        try {
            console.log("Fetching response for ID:", responseId);
            const data = await fetchSurveyResponseById(responseId!);
            console.log("Fetched Response Data:", data);
            
            // Sort answers by question order
            if (data.answers) {
                console.log("Answers found:", data.answers.length);
                data.answers.sort((a, b) => (a.question?.order_index || 0) - (b.question?.order_index || 0));
            } else {
                console.warn("No answers found in response object");
            }
            setResponse(data);

            // Load any consented attribute disclosure + the catalog for labels.
            const [shared, definitions] = await Promise.all([
                fetchDisclosureForResponse(responseId!),
                fetchAttributeDefinitions(),
            ]);
            setDisclosure(shared);
            setDefs(definitions);
        } catch (e) {
            console.error("Failed to load response", e);
        } finally {
            setLoading(false);
        }
    };

    const formatSharedValue = (v: any): string => {
        if (Array.isArray(v)) return v.join(', ');
        if (typeof v === 'boolean') return v ? 'Yes' : 'No';
        return String(v);
    };

    const labelForField = (id: string) =>
        defs.find((d) => d.id === id)?.label_en || id;

    // Helper to get localized text
    const getLocalizedText = (obj: LocalizedText | undefined) => {
        if (!obj) return 'Untitled';
        return obj.en || Object.values(obj)[0] || 'Untitled';
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-slate-900"><Loader2 className="animate-spin text-indigo-500" /></div>;
    }

    if (!response) {
        return <div className="min-h-screen flex items-center justify-center bg-slate-900 text-slate-400">Response not found.</div>;
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-300 max-w-4xl mx-auto pb-12">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
                <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors">
                    <ArrowLeft size={20} />
                </button>
                <div>
                    <h1 className="text-xl font-bold text-white">Response Details</h1>
                    <p className="text-slate-400 text-sm">ID: {response.id}</p>
                </div>
            </div>

            {/* Meta Card */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <h3 className="text-slate-500 text-xs uppercase font-bold mb-2">Survey</h3>
                    <p className="text-white font-bold text-lg">{getLocalizedText(response.survey?.title)}</p>
                </div>
                <div>
                    <h3 className="text-slate-500 text-xs uppercase font-bold mb-2">Respondent</h3>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400">
                            <User size={20} />
                        </div>
                        <div>
                            <p className="text-white font-medium">{response.user?.full_name || 'Anonymous'}</p>
                            <p className="text-xs text-slate-500">{response.user?.email || response.user_id || 'Guest'}</p>
                        </div>
                    </div>
                </div>
                <div>
                    <h3 className="text-slate-500 text-xs uppercase font-bold mb-2">Status</h3>
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 inline-flex items-center gap-1">
                        <CheckCircle2 size={12} /> {response.status}
                    </span>
                </div>
                <div>
                    <h3 className="text-slate-500 text-xs uppercase font-bold mb-2">Submitted At</h3>
                    <div className="flex items-center gap-2 text-slate-300">
                        <Calendar size={16} className="text-slate-500" />
                        {new Date(response.completed_at).toLocaleString()}
                    </div>
                </div>
            </div>

            {/* Shared private data (consented disclosure) */}
            {disclosure && Object.keys(disclosure.disclosed || {}).length > 0 && (
                <div className="bg-emerald-900/10 rounded-xl border border-emerald-500/30 p-6">
                    <h2 className="text-lg font-bold text-emerald-300 flex items-center gap-2 mb-1">
                        <ShieldCheck size={18} /> Shared Private Data
                    </h2>
                    <p className="text-emerald-200/70 text-xs mb-4">
                        Disclosed with the respondent's explicit consent from their encrypted vault.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {Object.entries(disclosure.disclosed).map(([key, val]) => (
                            <div key={key} className="bg-slate-900/50 rounded-lg border border-slate-800 px-4 py-3">
                                <p className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                                    {labelForField(key)}
                                </p>
                                <p className="text-white text-sm mt-1">{formatSharedValue(val)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Answers */}
            <div className="space-y-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <FileText className="text-indigo-400" /> Answers
                </h2>
                
                {response.answers?.map((answer, index) => (
                    <div key={answer.id} className="bg-slate-800/50 rounded-xl border border-slate-700 p-6 hover:border-slate-600 transition-colors">
                        <div className="flex items-start gap-4">
                            <span className="bg-slate-700 text-slate-300 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0">
                                {index + 1}
                            </span>
                            <div className="flex-1">
                                <h3 className="text-slate-200 font-medium mb-3 text-lg">
                                    {getLocalizedText(answer.question?.question_text)}
                                </h3>
                                
                                <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-800">
                                    <AnswerDisplay answer={answer} />
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                {(!response.answers || response.answers.length === 0) && (
                    <div className="text-center py-12 text-slate-500 italic bg-slate-800/30 rounded-xl border border-slate-700 border-dashed">
                        No answers recorded for this response.
                    </div>
                )}
            </div>
        </div>
    );
};

const AnswerDisplay = ({ answer }: { answer: any }) => {
    const type = answer.question?.question_type;
    const value = answer.answer_value;

    if (value === null || value === undefined) return <span className="text-slate-500 italic">No Answer</span>;

    switch (type) {
        case 'GEO_LOCATION':
            return (
                <div className="flex items-center gap-2 text-emerald-400">
                    <MapPin size={18} />
                    <span className="font-mono">{value.lat}, {value.lng}</span>
                    {value.accuracy && <span className="text-slate-500 text-xs">(±{Math.round(value.accuracy)}m)</span>}
                </div>
            );
        case 'IMAGE_UPLOAD':
            return (
                <div className="relative group max-w-sm">
                    <img src={value} alt="User Upload" className="rounded-lg border border-slate-700" />
                    <a href={value} target="_blank" rel="noopener noreferrer" className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg text-white font-bold">
                        View Full Size
                    </a>
                </div>
            );
        case 'CHECKBOX':
            return (
                <div className="flex flex-wrap gap-2">
                    {(value as string[]).map((v, i) => (
                        <span key={i} className="px-2 py-1 bg-indigo-900/30 text-indigo-300 rounded border border-indigo-500/30 text-sm">
                            {v}
                        </span>
                    ))}
                </div>
            );
        case 'MATRIX':
            return (
                <div className="grid gap-2 text-sm">
                    {Object.entries(value as Record<string, string>).map(([rowId, colId]) => (
                        <div key={rowId} className="flex justify-between border-b border-slate-800 pb-1 last:border-0">
                            <span className="text-slate-400">Row: {rowId}</span>
                            <span className="text-white font-medium">{colId}</span>
                        </div>
                    ))}
                </div>
            );
        case 'RANKING':
             return (
                <ol className="list-decimal list-inside space-y-1 text-slate-300">
                    {(value as string[]).map((v, i) => (
                        <li key={i} className="pl-2">{v}</li>
                    ))}
                </ol>
             );
        default:
            if (typeof value === 'object') {
                return (
                    <pre className="text-slate-300 text-xs bg-slate-900 p-2 rounded overflow-auto max-h-40">
                        {JSON.stringify(value, null, 2)}
                    </pre>
                );
            }
            return <span className="text-slate-200 whitespace-pre-wrap">{String(value)}</span>;
    }
};

export default SurveyResponseResultView;
